import { useState } from "react";
import { Heart, MessageCircle, Send } from "lucide-react";

const IMAGE_BASE_URL = "https://demouptask.visiontrek.io";

export default function UpcomingEventsModal({ isOpen, onClose, birthdays = [], anniversaries = [], onLike, onComment }) {
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commentText, setCommentText] = useState("");

  if (!isOpen) return null;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    });

  const today = new Date();
  const allEvents = [
    ...birthdays.map(b => ({ ...b, eventType: 'birthday' })),
    ...anniversaries.map(a => ({ ...a, eventType: 'anniversary' }))
  ].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

  const handleSendComment = (id) => {
    if (!commentText.trim()) return;
    onComment(id, commentText);
    setCommentText("");
    setActiveCommentId(null);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm">

      {/* Glass Card */}
      <div className="
        w-[500px] max-h-[85vh] overflow-hidden
        rounded-2xl
        backdrop-blur-xl
        bg-white/20
        border border-white/30
        shadow-[0_8px_32px_rgba(0,0,0,0.25)]
        p-6
        text-white
      ">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300 bg-clip-text text-transparent">
            ✨ Today & Upcoming Events
          </h2>

          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-lg"
          >
            ✕
          </button>
        </div>

        {/* List */}
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 no-scrollbar">

          {allEvents.flatMap((event) => {
            const isBirthday = event.eventType === 'birthday';

            if (!isBirthday && event.meta?.users) {
              return event.meta.users.map(u => ({
                ...event,
                ...u,
                id: `${event.id}-${u.id}`,
                _originalId: event.id,
                _feedItem: event,
                displayName: `${u.first_name} ${u.last_name}`,
                years: u.years_completed
              }));
            }

            return [{
              ...event,
              _originalId: event.id,
              _feedItem: event,
              displayName: `${event.meta?.first_name} ${event.meta?.last_name}`,
              years: event.meta?.years_completed
            }];
          }).map((event) => {
            const isBirthday = event.eventType === 'birthday';
            const eventDate = new Date(event.event_date);
            const isToday =
              today.getDate() === eventDate.getDate() &&
              today.getMonth() === eventDate.getMonth();

            let subtext = "";
            if (isBirthday) {
              const age = new Date().getFullYear() - new Date(event.dob || event.event_date).getFullYear();
              subtext = `Turning ${age} • ${isToday ? "Today 🎉" : formatDate(event.event_date)}`;
            } else {
              const years = event.years || 1;
              if (isToday) {
                subtext = `Completed ${years} Year${years > 1 ? 's' : ''} • Today 🎉`;
              } else {
                subtext = `Completing ${years} Year${years > 1 ? 's' : ''} • ${formatDate(event.event_date)}`;
              }
            }

            return (
              <div key={event.id} className="space-y-2">
                <div
                  className="
                    flex items-center gap-3
                    p-3 rounded-xl
                    bg-white/10
                    border border-white/20
                    hover:bg-white/20
                    transition
                  "
                >
                  {/* Avatar */}
                  <div className="relative">
                    <img
                      src={`${IMAGE_BASE_URL}${event.profile_pic || event.publisher_profile_pic || '/default-avatar.png'}`}
                      alt=""
                      className={`
                        w-11 h-11 rounded-full object-cover
                        ring-2 ${isBirthday ? 'ring-pink-300/60' : 'ring-blue-300/60'}
                      `}
                    />
                    <div className="absolute -bottom-1 -right-1 text-[10px] bg-white/20 backdrop-blur-md rounded-full p-0.5 border border-white/20">
                      {isBirthday ? "🎂" : "🏅"}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {event.displayName}
                    </p>

                    <p className="text-xs text-white/70">
                      {subtext}
                    </p>
                  </div>

                  {/* Social Actions */}
                  <div className="flex items-center gap-4 mr-2">
                    <button
                      onClick={() => onLike(event._feedItem)}
                      className={`flex flex-col items-center transition-colors ${event._feedItem.is_liked ? 'text-rose-400' : 'text-white/50 hover:text-rose-300'}`}
                    >
                      <Heart size={16} fill={event._feedItem.is_liked ? "currentColor" : "none"} />
                      <span className="text-[9px] font-bold mt-0.5">{event._feedItem.total_likes || 0}</span>
                    </button>

                    <button
                      onClick={() => {
                        setActiveCommentId(activeCommentId === event.id ? null : event.id);
                        setCommentText("");
                      }}
                      className={`flex flex-col items-center transition-colors ${activeCommentId === event.id ? 'text-cyan-400' : 'text-white/50 hover:text-cyan-300'}`}
                    >
                      <MessageCircle size={16} />
                      <span className="text-[9px] font-bold mt-0.5">{event._feedItem.comments?.length || 0}</span>
                    </button>
                  </div>

                  {/* Status Emoji */}
                  {isToday && (
                    <div className="animate-bounce">🎉</div>
                  )}
                </div>

                {/* Inline Comment Box */}
                {activeCommentId === event.id && (
                  <div className="flex gap-2 px-2 animate-in slide-in-from-top-2 duration-200">
                    <input
                      autoFocus
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendComment(event._feedItem.id)}
                      placeholder="Say something nice..."
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    />
                    <button
                      onClick={() => handleSendComment(event._feedItem.id)}
                      disabled={!commentText.trim()}
                      className="p-1.5 bg-cyan-600 rounded-lg disabled:opacity-50"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {allEvents.length === 0 && (
            <p className="text-center text-white/70 text-sm py-6">
              No upcoming events
            </p>
          )}

        </div>

      </div>
    </div>
  );
}
